'use client'

import React, { useEffect, useState } from 'react'
import { useAuth } from '@/providers/Auth'

type Stats = { active: number; total: number }

/**
 * Shows a live "active / total" user count in the top bar.
 * Renders nothing if the logged-in user is not an admin.
 * Polls every 60 seconds so the number stays fresh without a page reload.
 */
export function AdminUserStats() {
  const { user } = useAuth()
  const isAdmin = Array.isArray(user?.roles) && user.roles.includes('admin')
  const [stats, setStats] = useState<Stats | null>(null)

  useEffect(() => {
    if (!isAdmin) return

    const fetchStats = async () => {
      try {
        const res = await fetch('/api/admin/user-stats', { credentials: 'include' })
        if (res.ok) setStats(await res.json())
      } catch {
        // silently ignore — non-critical UI
      }
    }

    fetchStats()
    const interval = setInterval(fetchStats, 60_000) // refresh every 60 s
    return () => clearInterval(interval)
  }, [isAdmin])

  if (!isAdmin || !stats) return null

  const hasActive = stats.active > 0

  return (
    <>
      <span className="opacity-30">|</span>
      <span
        className="flex items-center gap-1.5 tabular-nums"
        title={`${stats.active} user${stats.active !== 1 ? 's' : ''} active in the last 15 min`}
      >
        {/* Pulse dot — green when active, dim when zero */}
        <span
          className={[
            'w-1.5 h-1.5 rounded-full flex-shrink-0',
            hasActive
              ? 'bg-green-400 shadow-[0_0_6px_2px_rgba(74,222,128,0.6)] animate-pulse'
              : 'bg-white/20',
          ].join(' ')}
        />
        <span className="text-white/60">
          <span className={hasActive ? 'text-green-400 font-semibold' : 'text-white/40'}>
            {stats.active}
          </span>
          <span className="text-white/25">/{stats.total}</span>
          <span className="text-white/40 ml-1 tracking-wider uppercase text-[10px]">online</span>
        </span>
      </span>
    </>
  )
}
